import { Dispatch, SetStateAction, useRef } from 'react';
import './PDFGen.css';
import { Property } from '../../../../models/Property';
import {
  Modal,
  ModalConfig,
  ModalContent,
  ModalFooter,
  ModalPositionX,
  ModalPositionY,
  SecondaryButton,
  PrimaryButton
} from '../../../shared/modal/Modal';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { propertyNameFormatter } from '../../../../utilities/propertyUtilities';
import { fullScreen, openInNewWindow } from '../pdf-utils/pdf-modal-utils';
import Loading from "../../../shared/loading/Loading";
import usePropertyDocument, { ResizerStatus } from "../pdf-utils/use-property-document";

interface PDFGenProps {
  property: Property;
  showmodal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

const PropertyDocumentConfig: ModalConfig = {
  title: "Summary PDF",
  showHeader: true,
  positionX: ModalPositionX.center,
  positionY: ModalPositionY.center,
  showOverlay: true
};

export default function PDFGen({ property, showmodal, setShowModal }: PDFGenProps) {
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  const {
    resizerState,
    propertyDocument
  } = usePropertyDocument(property);

  const openInNewWindowHandler = async () => {
    if (resizerState !== "success" || !propertyDocument) {
      console.warn("Images are not ready yet");
      return;
    }

    try {
      const blob = await pdf(propertyDocument).toBlob();
      openInNewWindow(blob, propertyNameFormatter(property));
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  };

  const fullScreenHandler = () => {
    const ref = modalContentRef.current;
    fullScreen(ref);
  };

  return (
    <Modal
      show={showmodal}
      setShow={setShowModal}
      config={PropertyDocumentConfig}
      resetPdfSrc={() => { }}
    >
      <ModalContent
        ref={modalContentRef}
        height={'calc(100vh - 385px)'}
        style={{ overflow: 'hidden' }}
      >
        <PropertyPDFPreview 
          resizerState={resizerState} 
          propertyDocument={propertyDocument} />
      </ModalContent>
      <ModalFooter>
        <SecondaryButton
          title="New Window"
          icon="esri-icon-launch-link-external"
          onClick={openInNewWindowHandler}
          disabled={resizerState !== "success"}
        />
        <PrimaryButton
          title="Full Screen"
          icon="esri-icon-zoom-out-fixed"
          onClick={fullScreenHandler}
        />
      </ModalFooter>
    </Modal>
  );
}

interface PropertyPDFPreviewProps {
  resizerState: ResizerStatus;
  propertyDocument: JSX.Element | undefined;
}

function PropertyPDFPreview({ resizerState, propertyDocument }: PropertyPDFPreviewProps) {
  switch (resizerState) {
    case "success":
      return (
        <PDFViewer height="100%" width="100%">
          {propertyDocument}
        </PDFViewer>
      );

    case "error":
      return <div className="m-auto">Error loading Summary PDF</div>;

    case "inProgress":
    case "idle":
    default:
      return (
        <Loading
          loadingContainerClass="m-auto"
          style={{ flexDirection: "column" }}
        />
      );
  }
}
